import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { ROUTES } from '@elara/client-core/constants';
import { matchPath } from '@elara/client-core/lib/route-utils';

import { sentry } from '#lib/sentry.js';
import {
  useAuthFormState,
  useMembershipPaymentState,
  useOnboardingState,
  usePhoneLinkState,
  usePhoneRequirementState,
  useRouterState,
  useSessionState,
  useUserProfileState,
} from '#states/hooks.js';
import { useSidebarPreferenceObserver, useThemeObserver } from '#ui/hooks/useThemeObserver.js';
import { Loader, Toaster } from '#ui/index.js';
import { Navigate } from '#views/Router.jsx';

import { PageNotFound } from './error/PageNotFound.jsx';
import { RouteErrorFallback } from './error/RouteErrorFallback.jsx';
import { PhoneVerificationDialog } from './profiles/PhoneVerificationDialog.js';
import { PolicyAcceptanceDialog } from './session/PolicyAcceptanceDialog.jsx';
import { BookingDetailsDialog } from './trip/BookingDetailsDialog.js';
import { DeleteItineraryItemDialog } from './trip/DeleteItineraryItemDialog.js';
import { ViewBookingDialog } from './trip/ViewBookingDialog.js';

/** Renders the first registered route matching the current path, or the 404 page. */
function MatchedRoute({ routerState }: { routerState: ReturnType<typeof useRouterState> }) {
  const currentPath = routerState.currentRoute?.path;
  if (!currentPath) return undefined;

  for (const routeConfig of routerState.routes.values()) {
    const match = matchPath(currentPath, routeConfig.path);

    if (match) {
      const Component = routeConfig.component;
      // The trip page and its panel variant (/trip/:id <-> /trip/:id/:panel) render the same
      // component; share a stable key so switching panels doesn't unmount/remount it (ELARA-704).
      const key =
        routeConfig.path === ROUTES.TRIP || routeConfig.path === ROUTES.TRIP_PANEL ? 'trip-detail' : routeConfig.path;
      return <Component key={key} />;
    }
  }

  // No routes matched - render 404 page
  return <PageNotFound />;
}

/** Route rendering with error boundary. */
function RouteBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary
      FallbackComponent={RouteErrorFallback}
      onError={(error, errorInfo) => {
        console.error('Route rendering error:', error, errorInfo);
        sentry?.captureException(error);
      }}
      onReset={() => {
        window.location.reload();
      }}
    >
      {children}
    </ErrorBoundary>
  );
}

export function App() {
  const sessionState = useSessionState();
  const authFormState = useAuthFormState();
  const routerState = useRouterState();
  const onboardingState = useOnboardingState();
  const membershipPaymentState = useMembershipPaymentState();
  const phoneRequirementState = usePhoneRequirementState();
  const phoneLinkState = usePhoneLinkState();
  const userProfileState = useUserProfileState();
  const user = sessionState.user;

  // Apply the current theme and watch for system changes when needed
  useThemeObserver();
  // Sync sidebar preference from user profile
  useSidebarPreferenceObserver();

  // Check if current route requires authentication
  const currentRouteConfig = routerState.getCurrentRouteConfig();
  const requiresAuth = currentRouteConfig?.requiresAuth ?? false;

  // Standalone public pages (a shared itinerary) render before every signed-in gate below: they
  // don't wait for auth or the profile, and no login/payment/onboarding redirect or policy dialog
  // applies — a signed-out friend and a signed-in member both see the page as-is.
  if (currentRouteConfig?.standalone) {
    return (
      <>
        <Toaster />
        <RouteBoundary>
          <MatchedRoute routerState={routerState} />
        </RouteBoundary>
      </>
    );
  }

  // Wait for both auth AND profile to resolve before rendering route guards.
  // Without this, onboarding guards can briefly evaluate in an indeterminate state
  // (profile not yet loaded), causing a flash of the onboarding flow for returning users.
  if (sessionState.isLoading || (sessionState.isAuthenticated && userProfileState.isLoading)) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Loader loading={true} />
      </div>
    );
  }

  // Check if current route is an auth page
  const currentPath = routerState.currentRoute?.path;
  const isAuthPage =
    currentPath === ROUTES.LOGIN ||
    currentPath === ROUTES.SIGNUP ||
    currentPath === ROUTES.FORGOT_PASSWORD ||
    currentPath === ROUTES.RESET_PASSWORD ||
    currentPath === ROUTES.FIREBASE_ACTION;
  const isOnboardingPage = currentPath === ROUTES.ONBOARDING;
  const isPaymentPage = currentPath === ROUTES.MEMBERSHIP_PAYMENT;
  const isVerifyPhonePage = currentPath === ROUTES.VERIFY_PHONE;

  /**
   * Hold the auth-page redirect below while a signup is mid-sequence.
   *
   * Firebase flips the auth state as soon as the account is created, but signup is not finished —
   * the profile is created next, and it is the profile that says whether this membership owes a
   * payment. Redirecting on `user` alone lands the member on home and, because the profile has not
   * arrived, a frame of onboarding, before anything can decide where they actually belong.
   *
   * Both clauses matter. `isSigningUp` is what separates this from an account whose profile never
   * landed and genuinely needs onboarding's recovery create; `!profile` is what ends the hold, since
   * the flag is deliberately never cleared (a failed profile create has to keep its error on the
   * signup form rather than be bounced away from it).
   */
  const isSignupSettling = currentPath === ROUTES.SIGNUP && authFormState.isSigningUp && !userProfileState.profile;

  return (
    <>
      <Toaster />
      <DeleteItineraryItemDialog />
      <BookingDetailsDialog />
      <ViewBookingDialog />
      {user && <PhoneVerificationDialog />}
      {user && !isAuthPage && !isOnboardingPage && !isPaymentPage && !routerState.isInAdmin && (
        <PolicyAcceptanceDialog />
      )}

      {/* Redirect authenticated users away from auth pages */}
      {user && isAuthPage && !isSignupSettling && <Navigate to={ROUTES.HOME} replace />}

      {/* Redirect unauthenticated users trying to access protected routes */}
      {!user && requiresAuth && <Navigate to={ROUTES.LOGIN} replace />}

      {/*
        Payment gate. An account with no subscription has no app to be onboarded into, so the card
        comes before onboarding. Reached from signup and from any later session — a member who
        abandoned at the card fields lands back here rather than inside.

        Its precedence over the onboarding gate is the `!isLocked` clause on that gate below,
        NOT this one appearing first: `Navigate` acts in an effect, so when both render in the same
        pass both effects run in tree order and the LAST one wins. Ordering these by position sent a
        member who owed payment to onboarding instead.

        `isLocked` reads the shared domain decision off the profile, so it cannot drift from what the
        server's flow gate enforces. It holds for BOTH remedies — a first payment owed, or a lapsed
        membership — and the page picks between the card form and the billing portal. It is false
        for a comped membership: a free-lifetime member never sees this screen.
      */}
      {user && !isAuthPage && !isPaymentPage && !routerState.isInAdmin && membershipPaymentState.isLocked && (
        <Navigate to={ROUTES.MEMBERSHIP_PAYMENT} replace />
      )}

      {/* Release the payment page as soon as there is nothing owed — payment settled, a membership
          repaired through the portal, or one that never owed anything. */}
      {user && isPaymentPage && !membershipPaymentState.isLocked && <Navigate to={ROUTES.HOME} replace />}

      {/* Redirect users who haven't completed onboarding — but never over an unpaid membership; see
          the payment gate above for why that precedence has to be a condition and not an ordering. */}
      {user &&
        !isAuthPage &&
        !isOnboardingPage &&
        !isPaymentPage &&
        !routerState.isInAdmin &&
        !membershipPaymentState.isLocked &&
        onboardingState.shouldShowOnboarding && <Navigate to={ROUTES.ONBOARDING} replace />}

      {/*
        Phone gate (ELARA-1170). A member more than a day old with no verified phone is held on the
        verification page. No precedence condition is needed against the gates above: the requirement
        is already false while a payment is owed, onboarding is unfinished, or a policy update is
        unaccepted, so it never renders alongside them.
      */}
      {user && !isAuthPage && !isVerifyPhonePage && !routerState.isInAdmin && phoneRequirementState.isRequired && (
        <Navigate to={ROUTES.VERIFY_PHONE} replace />
      )}

      {/* Release the verification page once nothing is required, except straight after verifying, when
          the page shows its success view until the member continues. */}
      {user && isVerifyPhonePage && !phoneRequirementState.isRequired && phoneLinkState.status !== 'verified' && (
        <Navigate to={ROUTES.HOME} replace />
      )}

      {/* Redirect users who have completed onboarding away from the onboarding page */}
      {user && isOnboardingPage && onboardingState.isOnboardingComplete && <Navigate to={ROUTES.HOME} replace />}

      <RouteBoundary>
        <MatchedRoute routerState={routerState} />
      </RouteBoundary>
    </>
  );
}
